"use client";

import { useState, useEffect, useCallback } from "react";
import { IoClose } from "react-icons/io5";
import Slider from "@mui/material/Slider";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { useTransition } from "react";
import styles from "./FilterMenu.module.scss";
import Accordion from "@/components/fashion/common/Accordian";
import { formatCurrencyWithSign } from "@/lib/utils";

// ─── helpers ────────────────────────────────────────────────────────────────

function buildQueryString(searchParams, selected, priceRange, priceFilter) {
  const params = new URLSearchParams(searchParams.toString());

  ["brand", "attribute_name", "attribute_value", "option_name", "option_value", "price"].forEach((k) => params.delete(k));

  const defaultMin = priceFilter?.min ?? 1000;
  const defaultMax = priceFilter?.max ?? 89000;
  if (priceRange[0] !== defaultMin || priceRange[1] !== defaultMax) {
    params.set("price", `${priceRange[0]}-${priceRange[1]}`);
  }

  const brandEntry = selected["brand::Brand"];
  if (brandEntry?.size) params.set("brand", [...brandEntry].join("+"));

  let base = params.toString();

  // One option_name/option_value pair per filter group (repeated params)
  for (const [filterKey, handles] of Object.entries(selected)) {
    const [type, title] = filterKey.split("::");
    if (!handles.size) continue;
    const values = [...handles].map(encodeURIComponent).join("%2B");
    if (type === "option") {
      const str = `option_name=${encodeURIComponent(title)}&option_value=${values}`;
      base = base ? `${base}&${str}` : str;
    } else if (type === "attribute") {
      const str = `attribute_name=${encodeURIComponent(title)}&attribute_value=${values}`;
      base = base ? `${base}&${str}` : str;
    }
  }

  return base;
}

function parseSelectedFromUrl(searchParams) {
  const selected = {};

  // brand
  const brandParam = searchParams.get("brand");
  if (brandParam) {
    selected["brand::Brand"] = new Set(brandParam.split("+").filter(Boolean));
  }

  // option_name=Storage&option_value=512GB%2B128GB (one pair per filter, repeated)
  const optNames  = searchParams.getAll("option_name");
  const optValues = searchParams.getAll("option_value");
  optNames.forEach((name, i) => {
    selected[`option::${name}`] = new Set((optValues[i] || "").split("+").filter(Boolean));
  });

  // attribute_name=Color&attribute_value=Pink%2BYellow (one pair per filter, repeated)
  const attrNames  = searchParams.getAll("attribute_name");
  const attrValues = searchParams.getAll("attribute_value");
  attrNames.forEach((name, i) => {
    selected[`attribute::${name}`] = new Set((attrValues[i] || "").split("+").filter(Boolean));
  });

  return selected;
}

// ─── component ──────────────────────────────────────────────────────────────

export default function FilterMenu({ isOpen, onClose, filters = [], currency }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [, startTransition] = useTransition();

  const priceFilter = filters.find((f) => f.type === "price") ?? { min: 1000, max: 89000 };

  const [priceRange, setPriceRange] = useState([
    priceFilter.min ?? 1000,
    priceFilter.max ?? 89000,
  ]);
  const [inputMin, setInputMin] = useState(priceFilter.min ?? 1000);
  const [inputMax, setInputMax] = useState(priceFilter.max ?? 89000);

  const [selected, setSelected] = useState(() => parseSelectedFromUrl(searchParams));
  const [openAccordion, setOpenAccordion] = useState("price");

  useEffect(() => {
    setSelected(parseSelectedFromUrl(searchParams));
    const priceParam = searchParams.get("price");
    if (priceParam) {
      const [min, max] = priceParam.split("-").map(Number);
      if (!isNaN(min) && !isNaN(max)) {
        setPriceRange([min, max]);
        setInputMin(min);
        setInputMax(max);
      }
    } else {
      setPriceRange([priceFilter.min ?? 1000, priceFilter.max ?? 89000]);
      setInputMin(priceFilter.min ?? 1000);
      setInputMax(priceFilter.max ?? 89000);
    }
  }, [searchParams]);

  useEffect(() => {
    const body = document.body;
    if (body) body.style.overflowY = isOpen ? "hidden" : "";
    return () => { body.style.overflowY = ""; };
  }, [isOpen]);

  const pushFilters = useCallback(
    (nextSelected, nextPrice) => {
      const qs = buildQueryString(searchParams, nextSelected, nextPrice, priceFilter);
      startTransition(() => {
        router.push(`${pathname}?${qs}`, { scroll: false });
        router.refresh();
      });
    },
    [searchParams, pathname, priceFilter, router]
  );

  // Apply immediately on checkbox change then close
  const handleToggle = (filterKey, handle) => {
    setSelected((prev) => {
      const next = { ...prev };
      const current = new Set(next[filterKey] ?? []);
      if (current.has(handle)) {
        current.delete(handle);
      } else {
        current.add(handle);
      }
      if (current.size === 0) {
        delete next[filterKey];
      } else {
        next[filterKey] = current;
      }
      pushFilters(next, priceRange);
      onClose();
      return next;
    });
  };

  const handleApplyPrice = () => {
    const min = Number(inputMin);
    const max = Number(inputMax);
    const clamped = [
      Math.max(min, priceFilter.min ?? 1000),
      Math.min(max, priceFilter.max ?? 89000),
    ];
    setPriceRange(clamped);
    pushFilters(selected, clamped);
    onClose();
  };

  const handleResetPrice = () => {
    const def = [priceFilter.min ?? 1000, priceFilter.max ?? 89000];
    setPriceRange(def);
    setInputMin(def[0]);
    setInputMax(def[1]);
    pushFilters(selected, def);
    onClose();
  };

  const handleClear = () => {
    setSelected({});
    const def = [priceFilter.min ?? 1000, priceFilter.max ?? 89000];
    setPriceRange(def);
    setInputMin(def[0]);
    setInputMax(def[1]);
    startTransition(() => {
      router.push(pathname, { scroll: false });
      router.refresh();
    });
    onClose();
  };

  const isChecked = (filterKey, handle) => selected[filterKey]?.has(handle) ?? false;

  const activeCount = Object.values(selected).reduce((acc, s) => acc + s.size, 0);

  return (
    <>
      {isOpen && <div className={styles.overlay} onClick={onClose} />}

      <div className={`${styles.filterMenu} ${isOpen ? styles.open : ""}`}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            FILTERS {activeCount > 0 && <span className={styles.activeCount}>({activeCount})</span>}
          </h2>
          <div className={styles.headerRight}>
            <button className={styles.clearButton} onClick={handleClear}>
              Clear Filter
            </button>
            <button className={styles.closeButton} onClick={onClose}>
              <IoClose size={20} />
            </button>
          </div>
        </div>

        <div className={styles.separator} />

        <div className={styles.content}>
          {/* Price */}
          <Accordion
            title="PRICE"
            isOpen={openAccordion === "price"}
            onToggle={() => setOpenAccordion(openAccordion === "price" ? null : "price")}
          >
            <div className={styles.priceFilter}>
              <div className={styles.priceLabels}>
                <span>{formatCurrencyWithSign(priceRange[0], currency)}</span>
                <span>{formatCurrencyWithSign(priceRange[1], currency)}</span>
              </div>
              <div style={{ padding: "0 10px" }}>
                <Slider
                  value={priceRange}
                  onChange={(_, v) => { setPriceRange(v); setInputMin(v[0]); setInputMax(v[1]); }}
                  min={priceFilter.min ?? 1000}
                  max={priceFilter.max ?? 89000}
                  sx={{
                    color: "#EE3C24",
                    "& .MuiSlider-thumb": { width: 16, height: 16 },
                    "& .MuiSlider-track": { height: 3 },
                    "& .MuiSlider-rail": { height: 3, backgroundColor: "#E7E7E7" },
                  }}
                />
              </div>
              <div className={styles.priceInputRow}>
                <div className={styles.priceInputGroup}>
                  <span className={styles.priceInputLabel}>Min</span>
                  <input
                    type="number"
                    className={styles.priceInput}
                    value={inputMin}
                    min={priceFilter.min ?? 1000}
                    max={priceFilter.max ?? 89000}
                    onChange={(e) => setInputMin(e.target.value)}
                  />
                </div>
                <span className={styles.priceSeparator}>-</span>
                <div className={styles.priceInputGroup}>
                  <span className={styles.priceInputLabel}>Max</span>
                  <input
                    type="number"
                    className={styles.priceInput}
                    value={inputMax}
                    min={priceFilter.min ?? 1000}
                    max={priceFilter.max ?? 89000}
                    onChange={(e) => setInputMax(e.target.value)}
                  />
                </div>
              </div>
              <div className={styles.priceActions}>
                <button className={styles.priceReset} onClick={handleResetPrice}>Reset</button>
                <button className={styles.priceApply} onClick={handleApplyPrice}>Apply</button>
              </div>
            </div>
          </Accordion>

          {/* Dynamic filters */}
          {filters.map((filter) => {
            if (filter.type === "price" || filter.type === "category") return null;
            if (!filter.data || filter.data.length === 0) return null;

            const filterKey = filter.type === "brand"
              ? `brand::Brand`
              : filter.type === "options"
              ? `option::${filter.title}`
              : `attribute::${filter.title}`;

            return (
              <Accordion
                key={filterKey}
                title={filter.title.toUpperCase()}
                isOpen={openAccordion === filterKey}
                onToggle={() => setOpenAccordion(openAccordion === filterKey ? null : filterKey)}
              >
                {filter.data.map((item) => (
                  <label
                    className={styles.checkboxLabel}
                    key={`${filterKey}-${item.handle}`}
                  >
                    {item.hex && (
                      <span
                        className={styles.colorSwatch}
                        style={{ backgroundColor: item.hex }}
                      />
                    )}
                    <input
                      type="checkbox"
                      checked={isChecked(filterKey, item.handle)}
                      onChange={() => handleToggle(filterKey, item.handle)}
                      className={styles.checkbox}
                    />
                    <span>{item.name}</span>
                  </label>
                ))}
              </Accordion>
            );
          })}
        </div>
      </div>
    </>
  );
}
